// Cap Table page component
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { useOrganization } from '@/lib/hooks/useOrganization'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import EquityCalculatorWidget from '@/components/custom-ui/equity-calculator-widget'
import { CapTableFormatter, type CapTableExportData } from '@/lib/cap-table-formatter'
import { ExportUtils } from '@/lib/export-utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface CapTableEntry {
  id: string
  user_id: string | null
  equity_class_id: string | null
  shares: number
  percentage: number
  funding_round_id: string | null
  users: {
    first_name: string | null
    last_name: string | null
    email: string
  } | null
  equity_classes: {
    class_name: string
    liquidation_preference_multiple: number | null
    participation_cap_multiple: number | null
    conversion_ratio: number | null
    seniority_rank: number | null
  } | null
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D']

export default function CapTablePage() {
  const getToken = useGetToken()
  const { currentProject } = useProject()
  useOrganization() // For side effects
  const [exporting, setExporting] = useState(false)
  
  const { data: entries, isLoading } = useQuery<CapTableEntry[]>({
    queryKey: ['cap-table', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('cap_table_entries')
        .select(`
          *,
          users (first_name, last_name, email),
          equity_classes (class_name, liquidation_preference_multiple, participation_cap_multiple, conversion_ratio, seniority_rank)
        `)
        .eq('project_id', currentProject.id)
        .order('percentage', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  // Fetch additional data for export
  const { data: fundingRounds } = useQuery({
    queryKey: ['funding-rounds-export', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('funding_rounds')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('round_date', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  const { data: valuations } = useQuery({
    queryKey: ['valuations-export', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('valuations')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('valuation_date', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  const { data: shareAuthorizations } = useQuery({
    queryKey: ['share-authorizations-export', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return null

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('projects')
        .select('total_shares_authorized, authorized_shares')
        .eq('id', currentProject.id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!currentProject
  })

  const chartData = entries?.map((entry: CapTableEntry) => ({
    name: entry.users ? `${entry.users.first_name || ''} ${entry.users.last_name || ''}`.trim() || entry.users.email : 'Unknown',
    value: Number(entry.percentage),
  })) || []

  const totalShares = entries?.reduce((sum: number, entry: CapTableEntry) => sum + Number(entry.shares), 0) || 0
  const totalPercentage = entries?.reduce((sum: number, entry: CapTableEntry) => sum + Number(entry.percentage), 0) || 0

  // Group by equity class
  const equityByClass = entries?.reduce((acc: Record<string, { shares: number; percentage: number; count: number }>, entry: CapTableEntry) => {
    const className = entry.equity_classes?.class_name || 'Unknown'
    if (!acc[className]) {
      acc[className] = { shares: 0, percentage: 0, count: 0 }
    }
    acc[className].shares += Number(entry.shares)
    acc[className].percentage += Number(entry.percentage)
    acc[className].count += 1
    return acc
  }, {} as Record<string, { shares: number; percentage: number; count: number }>) || {}

  // Prepare export data
  const prepareExportData = (): CapTableExportData | null => {
    if (!entries || !currentProject || !shareAuthorizations) return null

    const capTableData = entries.map((entry: CapTableEntry) => ({
      user_id: entry.user_id || '',
      shareholder: {
        id: entry.user_id || '',
        name: entry.users ? `${entry.users.first_name || ''} ${entry.users.last_name || ''}`.trim() || entry.users.email : 'Unknown',
        email: entry.users?.email || '',
        type: 'employee' as const
      },
      equity_class: {
        id: entry.equity_class_id || '',
        class_name: entry.equity_classes?.class_name || 'Unknown',
        class_type: 'common' as 'common' | 'preferred' | 'options' | 'warrants',
        liquidation_preference: entry.equity_classes?.liquidation_preference_multiple || null,
        participation_cap_multiple: entry.equity_classes?.participation_cap_multiple || null,
        conversion_ratio: entry.equity_classes?.conversion_ratio || null,
        seniority_rank: entry.equity_classes?.seniority_rank || null
      },
      shares: entry.shares,
      percentage: entry.percentage
    }))

    // Calculate issued shares
    const issuedSharesCalc = entries.reduce((acc: { common: number; preferred: number; options: number; warrants: number; total: number }, entry: CapTableEntry) => {
      const className = (entry.equity_classes?.class_name || 'common').toLowerCase()
      if (className.includes('preferred')) acc.preferred += entry.shares
      else if (className.includes('option')) acc.options += entry.shares
      else if (className.includes('warrant')) acc.warrants += entry.shares
      else acc.common += entry.shares
      acc.total += entry.shares
      return acc
    }, { common: 0, preferred: 0, options: 0, warrants: 0, total: 0 })
    const issuedShares = issuedSharesCalc

    return {
      companyName: currentProject.company_name || currentProject.name,
      exportDate: new Date(),
      capTable: capTableData,
      equityClasses: [...new Map(entries.map((e: CapTableEntry) => [e.equity_class_id, e.equity_classes])).values()].map((ec: any) => ({
        id: '',
        class_name: ec?.class_name || 'Unknown',
        class_type: 'common' as 'common' | 'preferred' | 'options' | 'warrants',
        liquidation_preference: ec?.liquidation_preference_multiple || null,
        participation_cap_multiple: ec?.participation_cap_multiple || null,
        conversion_ratio: ec?.conversion_ratio || null,
        seniority_rank: ec?.seniority_rank || null
      })),
      fundingRounds: fundingRounds || [],
      valuations: valuations || [],
      authorizedShares: {
        common: 0,
        preferred: 0,
        total: shareAuthorizations?.total_shares_authorized || shareAuthorizations?.authorized_shares || 0
      },
      issuedShares
    }
  }

  // Export handlers
  const handleExportExcel = async () => {
    try {
      setExporting(true)
      const exportData = prepareExportData()
      if (!exportData) {
        toast.error('Unable to prepare export data')
        return
      }
      await CapTableFormatter.exportToExcel(exportData)
      toast.success('Cap table exported to Excel')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export cap table')
    } finally {
      setExporting(false)
    }
  }

  const handleExportPDF = () => {
    try {
      setExporting(true)
      const exportData = prepareExportData()
      if (!exportData) {
        toast.error('Unable to prepare export data')
        return
      }
      CapTableFormatter.exportToPDF(exportData)
      toast.success('Cap table exported to PDF')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export cap table')
    } finally {
      setExporting(false)
    }
  }

  const handleExportCSV = () => {
    if (!entries) return

    const csvData = entries.map((entry: CapTableEntry) => ({
      name: entry.users ? `${entry.users.first_name || ''} ${entry.users.last_name || ''}`.trim() : 'Unknown',
      email: entry.users?.email || '',
      equity_class: entry.equity_classes?.class_name || 'Unknown',
      shares: entry.shares,
      percentage: `${entry.percentage}%`
    }))

    ExportUtils.exportToCSV(csvData, [
      { header: 'Name', key: 'name' },
      { header: 'Email', key: 'email' },
      { header: 'Equity Class', key: 'equity_class' },
      { header: 'Shares', key: 'shares' },
      { header: 'Ownership %', key: 'percentage' }
    ], {
      filename: `${currentProject?.name || 'cap-table'}_export`
    })
    
    toast.success('Cap table exported to CSV')
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Cap Table</h1>
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!entries || entries.length === 0 || exporting}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Export to PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileText className="h-4 w-4 mr-2" />
                Export to CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <EquityCalculatorWidget />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
          <div className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Ownership Distribution</h2>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                  >
                    {chartData.map((_entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-8">No equity data available</p>
            )}
          </div>

          {/* Summary Stats */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Summary</h2>
            <div className="space-y-4">
              <div className="border-b border-gray-200 pb-4">
                <p className="text-sm text-gray-500">Total Shares Outstanding</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {totalShares.toLocaleString()}
                </p>
              </div>
              <div className="border-b border-gray-200 pb-4">
                <p className="text-sm text-gray-500">Total Equity Allocated</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {totalPercentage.toFixed(2)}%
                </p>
              </div>
              <div className="border-b border-gray-200 pb-4">
                <p className="text-sm text-gray-500">Shareholders</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {entries?.length || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">By Equity Class</p>
                {Object.entries(equityByClass).map(([className, data]: [string, any]) => (
                  <div key={className} className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{className}:</span>
                    <span className="text-sm text-gray-900">
                      {data.percentage.toFixed(2)}% ({data.count} holders)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Detailed Breakdown</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Shareholder
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Class
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Shares
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ownership
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entries?.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  No cap table entries found.
                </td>
              </tr>
            ) : (
              entries?.map((entry: CapTableEntry) => (
                <tr key={entry.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {entry.users?.first_name || ''} {entry.users?.last_name || ''}
                      </div>
                      <div className="text-sm text-gray-500">{entry.users?.email || ''}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.equity_classes?.class_name || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number(entry.shares).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {Number(entry.percentage).toFixed(2)}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
